import { Injectable } from "@angular/core";
import { AngularFirestore } from "@angular/fire/compat/firestore";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { map } from "rxjs/operators";
import moment from 'moment-timezone/builds/moment-timezone-with-data-1970-2030.min.js';
import { ConfigService } from "src/app/shared/config";
import { NotificationService } from "src/app/shared/notification.service";
import { AuditService } from "src/app/services/audit.service";
import { AuthService } from "src/app/services/auth.service";
import { environment } from "src/environments/environment";

// Isolated admin data layer for the v2 (root-collection) membership model.
// (users-groups-root-migration — parallel-testing phase)
//
// SAFETY CONTRACT (mirrors the cloud-functions membershipService):
//   * Touches ONLY root collections: employees / groups / memberships.
//   * NEVER reads or writes a legacy nested company/{c}/sites/{s}/users|empGroups
//     doc. The existing legacy admin pages/services are completely separate.
//   * Deletes are SOFT (deleted:true flag); no document is ever removed.
//   * Adding a user goes through the /user2 cloud endpoint (race-safe global
//     employee create + server-side password hashing). All test data is
//     flagged isTest:true so it can be found and cleaned up.
@Injectable({ providedIn: 'root' })
export class MembershipV2Service {

    private httpOptions = {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    };

    constructor(
        private afs: AngularFirestore,
        private config: ConfigService,
        private notifier: NotificationService,
        private audit: AuditService,
        private authService: AuthService,
        private http: HttpClient
    ) { }

    // ---- reads (realtime, root collections, soft-deleted filtered in code) --

    getMemberships$(siteId: string) {
        return this.afs.collection('memberships', ref => ref
            .where('companyId', '==', this.config.getCompanyId())
            .where('siteId', '==', siteId))
            .snapshotChanges()
            .pipe(map(changes => changes
                .map(c => ({ dbid: c.payload.doc.id, ...(c.payload.doc.data() as any) }))
                .filter(m => m.deleted !== true)
                .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
            ));
    }

    getGroups$(siteId: string) {
        return this.afs.collection('groups', ref => ref
            .where('companyId', '==', this.config.getCompanyId())
            .where('siteId', '==', siteId))
            .snapshotChanges()
            .pipe(map(changes => changes
                .map(c => ({ dbid: c.payload.doc.id, ...(c.payload.doc.data() as any) }))
                .filter(g => g.deleted !== true)
            ));
    }

    // All (live) groups for the company across EVERY site — realtime. Powers the
    // global customer-mapping view (group -> kundekort), which isn't site-scoped.
    getGroupsByCompany$() {
        return this.afs.collection('groups', ref => ref
            .where('companyId', '==', this.config.getCompanyId()))
            .snapshotChanges()
            .pipe(map(changes => changes
                .map(c => ({ dbid: c.payload.doc.id, ...(c.payload.doc.data() as any) }))
                .filter(g => g.deleted !== true)
            ));
    }

    // Same as getGroupsByCompany$() but with NO companyId filter — every group,
    // every company. Super-admin only (gated in the component); `groups` has no
    // subcollection nesting so this is a plain flat read, no index/collectionGroup
    // needed.
    getAllGroupsLive$() {
        return this.afs.collection('groups')
            .snapshotChanges()
            .pipe(map(changes => changes
                .map(c => ({ dbid: c.payload.doc.id, ...(c.payload.doc.data() as any) }))
                .filter(g => g.deleted !== true)
            ));
    }

    // Memberships key on the group's human groupId, scoped by company + site.
    countGroupMembers(group: any): Promise<number> {
        if (!group?.groupId || !group?.companyId || !group?.siteId) { return Promise.resolve(0); }
        return this.afs.collection('memberships').ref
            .where('companyId', '==', group.companyId)
            .where('siteId', '==', group.siteId)
            .where('groupId', '==', group.groupId)
            .get()
            .then(s => s.docs.filter(d => (d.data() as any).deleted !== true).length);
    }

    // ---- global (cross-site) reads for the super-admin employee dashboard ---
    // Root collections are global, so these span every site/company. One-time
    // gets (not realtime) — the dashboard loads once then filters client-side.

    getAllEmployeesOnce(): Promise<any[]> {
        return this.afs.collection('employees').ref.get()
            .then(s => s.docs.map(d => ({ id: d.id, ...(d.data() as any) })));
    }

    getAllMembershipsOnce(): Promise<any[]> {
        return this.afs.collection('memberships').ref.get()
            .then(s => s.docs
                .map(d => ({ dbid: d.id, ...(d.data() as any) }))
                .filter(m => m.deleted !== true));
    }

    // all groups (global) as full docs — for the per-site group picker on edit
    getAllGroupsOnce(): Promise<any[]> {
        return this.afs.collection('groups').ref.get()
            .then(s => s.docs.map(d => ({ dbid: d.id, ...(d.data() as any) })).filter(g => g.deleted !== true));
    }

    // groupId -> group name (global)
    getGroupsMapOnce(): Promise<{ [id: string]: string }> {
        return this.afs.collection('groups').ref.get().then(s => {
            const m: { [id: string]: string } = {};
            s.docs.forEach(d => { const g = d.data() as any; if (g.groupId) { m[g.groupId] = g.name || g.groupId; } });
            return m;
        });
    }

    // companyId -> company name
    getCompaniesMapOnce(): Promise<{ [id: string]: string }> {
        return this.afs.collection('company').ref.get().then(s => {
            const m: { [id: string]: string } = {};
            s.docs.forEach(d => { const c = d.data() as any; m[d.id] = c.name || c.companyName || d.id; });
            return m;
        });
    }

    // siteId -> { name, companyId }. Iterates each company's `sites` subcollection
    // (allowed by the existing nested company/{c}/sites rule) instead of a
    // collectionGroup('sites') query — collection-group queries need a dedicated
    // `match /{path=**}/sites` rule the project doesn't have, which is what caused
    // the "Missing or insufficient permissions" error.
    async getSitesMapOnce(): Promise<{ [id: string]: { name: string, companyId: string } }> {
        const companies = await this.afs.collection('company').ref.get();
        const m: { [id: string]: { name: string, companyId: string } } = {};
        await Promise.all(companies.docs.map(async c => {
            const sites = await c.ref.collection('sites').get();
            sites.forEach(s => { m[s.id] = { name: (s.data() as any).name || s.id, companyId: c.id }; });
        }));
        return m;
    }

    getMembershipTransactions$(membershipId: string) {
        return this.afs.collection('memberships').doc(membershipId)
            .collection('transactions', ref => ref.orderBy('createdOn', 'desc'))
            .snapshotChanges()
            .pipe(map(changes => changes.map(c => ({
                dbid: c.payload.doc.id,
                ...(c.payload.doc.data() as any)
            }))));
    }

    // ---- users -------------------------------------------------------------

    // Race-safe create via the cloud endpoint (handles global-employee dedup +
    // bcrypt of the optional password). `isTest` defaults to false — real
    // employees are clean production data; pass true only to seed test users.
    // Race-safe create via the cloud endpoint. Resolves with the response so the
    // caller can react to a rejected duplicate (see `existing`); resolves rather
    // than rejects on failure — the endpoint answers 400 for "already exists",
    // which HttpClient routes to the error callback, and that callback used to
    // swallow the server's explanation behind a generic "Could not add employee".
    // notify=false: no toast per call (the bulk import shows its own per-row result).
    async addEmployee(siteId: string, payload: any, isTest: boolean = false, notify: boolean = true): Promise<any> {
        const body = {
            ...payload,
            companyId: this.config.getCompanyId(),
            siteId,
            isTest: isTest === true
        };
        // The endpoint is admin-operator only.
        const idToken = await this.authService.getToken();
        const options = { headers: new HttpHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` }) };
        return new Promise((res) => {
            this.http.post(environment.apiUrl + "/widgets/user2/addEmployeeMembership",
                JSON.stringify(body), options).subscribe((data: any) => {
                    if (data && data.success) {
                        if (!notify) {
                            // caller reports the result
                        } else if (body.sendInvite && !data.invited) {
                            this.notifier.showNotification('warning', `Employee added, but the welcome email to ${body.email} could not be sent`);
                        } else {
                            this.notifier.showNotification('success', body.sendInvite ? `Employee added — welcome email sent to ${body.email}` : "Employee added");
                        }
                        res(data);
                    } else {
                        if (notify) { this.notifier.showNotification('error', this.addEmployeeError(data)); }
                        res(data || { success: false });
                    }
                }, (err: any) => {
                    // Server's JSON body lives on err.error for non-2xx responses.
                    const data = err && err.error ? err.error : null;
                    if (notify) { this.notifier.showNotification('error', this.addEmployeeError(data)); }
                    res(data || { success: false });
                });
        });
    }

    // Turns the endpoint's duplicate rejection into something actionable: who
    // already occupies that email at this site, in which group, active or not.
    addEmployeeError(data: any): string {
        if (!data) { return "Could not add employee"; }
        const e = data.existing;
        if (!e) { return data.message || "Could not add employee"; }

        const who = [e.name, e.email].filter(x => !!x).join(' — ') || 'This employee';
        const parts = [`Group: ${e.groupName || e.groupId || 'none'}`, `Status: ${e.status ? 'Active' : 'Inactive'}`];
        if (e.employeeCode) { parts.push(`Employee no: ${e.employeeCode}`); }
        return `${who} is already registered at this site (${parts.join(', ')}). Edit the existing row instead of adding a new one.`;
    }

    // Super-admin sets an employee's password directly (hashed server-side).
    // Unlike a self-service forgot-password flow, this doesn't email the
    // employee — it applies immediately so the admin can hand them the new password.
    async adminResetPassword(employeeDocId: string, newPassword: string): Promise<boolean> {
        try {
            const idToken = await this.authService.getToken();
            const data: any = await this.http.post(
                environment.apiUrl + "/widgets/user2/adminResetPassword",
                JSON.stringify({ employeeDocId, newPassword }),
                { headers: new HttpHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` }) }
            ).toPromise();

            if (data && data.success) {
                this.notifier.showNotification('success', "Password reset");
                return true;
            }
            this.notifier.showNotification('error', (data && data.message) || "Could not reset password");
            return false;
        } catch (err) {
            console.log('adminResetPassword error', err);
            this.notifier.showNotification('error', (err.error && err.error.message) || err.message || "Could not reset password");
            return false;
        }
    }

    // Emails the employee a reset link instead of setting a password directly
    // — for when the admin would rather the employee pick their own password
    // than be handed a temporary one over the phone. Requires the operator's
    // own auth token, unlike adminResetPassword.
    async sendResetEmail(email: string): Promise<boolean> {
        try {
            const idToken = await this.authService.getToken();
            const data: any = await this.http.post(
                environment.apiUrl + "/widgets/user2/adminSendResetEmail",
                JSON.stringify({ email }),
                { headers: new HttpHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` }) }
            ).toPromise();

            if (data && data.success) {
                this.notifier.showNotification('success', `Reset email sent to ${email}`);
                return true;
            }
            this.notifier.showNotification('error', (data && data.message) || "Could not send the reset email");
            return false;
        } catch (err) {
            console.log('sendResetEmail error', err);
            this.notifier.showNotification('error', (err.error && err.error.message) || err.message || "Could not send the reset email");
            return false;
        }
    }

    updateMembership(membershipId: string, patch: any) {
        return this.afs.collection('memberships').doc(membershipId).update(patch)
            .then(() => this.notifier.showNotification('success', "Updated"))
            .catch(() => this.notifier.showNotification('error', "Update failed"));
    }

    // Manual credit adjust: writes the new balance on the membership + a ledger
    // entry in its transactions subcollection. Root-only, no legacy mirror.
    adjustCredit(membershipId: string, currentCredit: number, type: 'credit' | 'debit', amount: number, adminEmail: string) {
        const amt = Number(amount);
        if (!amt || amt <= 0) {
            this.notifier.showNotification('error', "Amount must be greater than 0");
            return Promise.reject();
        }
        const base = Number(currentCredit) || 0;
        if (type === 'debit' && amt > base) {
            this.notifier.showNotification('error', "Balance would go negative");
            return Promise.reject();
        }
        const balance = type === 'credit' ? base + amt : base - amt;
        const membershipRef = this.afs.collection('memberships').doc(membershipId);
        const txRef = membershipRef.collection('transactions').doc().ref;

        const batch = this.afs.firestore.batch();
        batch.update(membershipRef.ref, { credit: balance, renewalTime: moment().unix() });
        batch.set(txRef, {
            createdOn: moment().unix(),
            type,
            ammount: amt,
            balance,
            message: `Manual ${type} by ${adminEmail} (v2 test)`
        });
        return batch.commit()
            .then(() => this.notifier.showNotification('success', "Credit updated"))
            .catch(() => this.notifier.showNotification('error', "Credit update failed"));
    }

    // Soft delete — removes only this site membership, never the employee, never legacy.
    async softDeleteMembership(membershipId: string) {
        const reason = await this.audit.promptDeleteReason('membership', membershipId);
        if (reason === null) return; // cancelled
        return this.afs.collection('memberships').doc(membershipId).update({
            deleted: true,
            deletedAt: moment().unix(),
            status: false,
            ...this.audit.stamp('delete', reason)
        })
            .then(() => this.notifier.showNotification('success', "Membership removed (soft delete)"))
            .catch(() => this.notifier.showNotification('error', "Delete failed"));
    }

    // ---- groups ------------------------------------------------------------

    addGroup(siteId: string, payload: any, isTest: boolean = false) {
        const companyId = this.config.getCompanyId();
        const data = {
            ...payload,
            companyId,
            siteId,
            deleted: false,
            isTest: isTest === true,
            createdAt: moment().toISOString()
        };
        return this.afs.collection('groups').add(data)
            .then(ref => {
                // Schedule automatic credit renewal for creditRenew groups by
                // creating a `tasks` doc with the v2 worker (creditRenewV2). The
                // per-minute cron dispatches it to membershipService.creditRenew.
                // subsidyGroup applies live at checkout → no task.
                if (payload.worker === 'creditRenew') {
                    this.afs.collection('tasks').add({
                        options: { companyId, siteId, groupId: ref.id },
                        name: payload.name,
                        performAt: moment().endOf('month').hour(Number(payload.setHour) || 0).minute(Number(payload.setMinutes) || 0).unix(),
                        setHour: payload.setHour,
                        setMinutes: payload.setMinutes,
                        status: 'scheduled',
                        worker: 'creditRenewV2',
                        renewalMonthly: true,   // credit renewal is always monthly
                        isTest: isTest === true
                    });
                }
                this.notifier.showNotification('success', "Group added");
                return ref.id;
            })
            .catch(() => { this.notifier.showNotification('error', "Add group failed"); return null; });
    }

    updateGroup(groupDocId: string, patch: any) {
        return this.afs.collection('groups').doc(groupDocId).update(patch)
            .then(() => this.notifier.showNotification('success', "Group updated"))
            .catch(() => this.notifier.showNotification('error', "Group update failed"));
    }

    // Keep a group's creditRenewV2 renewal task in sync with an edit — updateGroup
    // changes the group doc but not its scheduled task. `group` must carry the
    // MERGED post-edit shape (siteId + worker + name + setHour/setMinutes/
    // renewalMonthly). For a Credit-Renew group we upsert the task (and dedupe any
    // extras); for anything else we DELETE it — status can't disable it because the
    // daily rescheduleTasks re-arms every task to 'scheduled' regardless.
    // Queries by options.groupId then filters worker in code so the group's LEGACY
    // `creditRenew` task (same options.groupId) is never touched.
    // `scheduleEdited` = the caller is the group dialog, where the admin actually
    // chose setHour/setMinutes/Monthly. Link/unlink/customer-assign leave an
    // existing task's schedule alone: groups migrated from legacy carried a
    // meaningless renewalMonthly:false, and copying it here turned monthly
    // renewals daily (Consat Norge AS, 2026-10).
    async syncRenewalTask(groupDocId: string, group: any, scheduleEdited = false) {
        // The group's own company, never the company selected in the admin:
        // Customer Mapping edits groups across every company, and a foreign
        // companyId makes the renewal match zero members.
        const companyId = group.companyId || this.config.getCompanyId();
        const db = this.afs.firestore;
        const snap = await db.collection('tasks').where('options.groupId', '==', groupDocId).get();
        const v2Tasks = snap.docs.filter(d => (d.data() as any).worker === 'creditRenewV2');
        const batch = db.batch();

        if (group.worker === 'creditRenew') {
            const taskData: any = {
                options: { companyId, siteId: group.siteId, groupId: groupDocId },
                name: group.name || '',
                worker: 'creditRenewV2'
            };
            // Credit renewal is always monthly; there is no daily option any more.
            const monthly = true;
            if (scheduleEdited || v2Tasks.length === 0) {
                taskData.setHour = group.setHour;
                taskData.setMinutes = group.setMinutes;
                taskData.renewalMonthly = monthly;
            }
            if (v2Tasks.length > 0) {
                // performAt/status are left alone: the nightly rescheduleTasks owns
                // them. Resetting performAt to "today at setHour" made every edit
                // fire an extra renewal that evening.
                v2Tasks.forEach((d, i) => i === 0
                    ? batch.set(d.ref, taskData, { merge: true })
                    : batch.delete(d.ref));   // dedupe stray duplicates
            } else {
                const performAt = monthly
                    ? moment().endOf('month').hour(Number(group.setHour) || 0).unix()
                    : moment().hour(Number(group.setHour) || 0).minute(Number(group.setMinutes) || 0).unix();
                const newRef = db.collection('tasks').doc();
                batch.set(newRef, { ...taskData, performAt, status: 'scheduled', createdAt: moment().toISOString() });
            }
        } else {
            // no longer a Credit-Renew group → remove its renewal task(s)
            v2Tasks.forEach(d => batch.delete(d.ref));
        }
        return batch.commit();
    }

    async softDeleteGroup(groupDocId: string) {
        const reason = await this.audit.promptDeleteReason('group', groupDocId);
        if (reason === null) return; // cancelled
        return this.afs.collection('groups').doc(groupDocId).update({
            deleted: true,
            deletedAt: moment().unix(),
            ...this.audit.stamp('delete', reason)
        })
            .then(() => this.notifier.showNotification('success', "Group removed (soft delete)"))
            .catch(() => this.notifier.showNotification('error', "Delete failed"));
    }

    // ---- invoicing (Phase B) -----------------------------------------------
    // Monthly group-sourced subsidy per customer (kundekort). Calls the cloud
    // /invoiceReport endpoint. dateFrom/dateTo are unix seconds.
    invoiceReport(dateFrom: number, dateTo: number, customerId?: string, companyId?: string) {
        const body: any = { companyId: companyId || this.config.getCompanyId(), dateFrom, dateTo };
        if (customerId) body.customerId = customerId;
        return this.http.post(environment.apiUrl + "/widgets/invoiceReport",
            JSON.stringify(body), this.httpOptions);
    }

    // Settlement reconciliation: send the normalized Vipps + Stripe rows (parsed
    // from the provider export files client-side) and let the cloud match them
    // against the period's transactions by orderID. dateFrom/dateTo are unix secs.
    // `companyId` override lets the caller fan this out across every company
    // (super-admin "all companies" view) instead of just the selected one.
    reconcileSettlements(dateFrom: number, dateTo: number, settlements: any[], companyId?: string) {
        // includeLines: also return one row per billed transaction — the
        // specification attached to each invoice (billing.component.ts).
        const body = { companyId: companyId || this.config.getCompanyId(), dateFrom, dateTo, settlements, includeLines: true };
        return this.http.post(environment.apiUrl + "/widgets/reconcileSettlements",
            JSON.stringify(body), this.httpOptions);
    }

    // Coffee cups per customer for a month, from the Vendon coffee machines
    // (vendonCoffeeBillable.js). The Vendon API key stays on the server.
    vendonCoffeeBillable(month: string) {
        return this.http.post(environment.apiUrl + "/widgets/vendonCoffeeBillable",
            JSON.stringify({ month }), this.httpOptions);
    }

    // Every Vendon coffee machine with its cups for a month plus its
    // vendon-machines config — the Coffee tab (vendonMachines.js).
    vendonMachines(month: string) {
        return this.http.post(environment.apiUrl + "/widgets/vendonMachines",
            JSON.stringify({ month }), this.httpOptions);
    }

    // Sends buildInvoices() output (billing.component.ts) to the cloud, which
    // creates a DRAFT invoice (SalesOrder) per customer in PowerOffice Go —
    // never auto-sent, a human reviews/sends each one inside PowerOffice Go.
    // PowerOffice endpoints require a super-admin login (powerOfficeAuth.js).
    private async powerOfficeHeaders() {
        const idToken = await this.authService.getToken();
        return { headers: new HttpHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` }) };
    }

    // Read-only dry run (powerOfficePreflight.js): what each invoice would become
    // in PowerOffice, including the VAT Go would charge per line.
    async preflightPowerOffice(invoices: any[]) {
        return this.http.post(environment.apiUrl + "/widgets/powerOfficePreflight",
            JSON.stringify({ invoices }), await this.powerOfficeHeaders()).toPromise();
    }

    // `env` must be the environment the preflight reported and the operator
    // confirmed; the server refuses a mismatch.
    async sendInvoicesToPowerOffice(invoices: any[], env: string) {
        return this.http.post(environment.apiUrl + "/widgets/powerOfficeSendInvoices",
            JSON.stringify({ invoices, environment: env }), await this.powerOfficeHeaders()).toPromise();
    }

    // Coffee turnover per system for a month (Vendon), counted toward the
    // garantiomsetning (garantiCoffeeTurnover.js).
    garantiCoffeeTurnover(month: string) {
        return this.http.post(environment.apiUrl + "/widgets/garantiCoffeeTurnover",
            JSON.stringify({ month }), this.httpOptions);
    }

    // Attach an invoice's transaction specification (.xlsx) to the draft sales
    // order powerOfficeSendInvoices created for it (powerOfficeAttachSpec.js).
    async attachSpecToPowerOffice(salesOrderId: string, fileName: string, fileBase64: string) {
        return this.http.post(environment.apiUrl + "/widgets/powerOfficeAttachSpec",
            JSON.stringify({ salesOrderId, fileName, fileBase64 }), await this.powerOfficeHeaders()).toPromise();
    }
}
